import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import "../index.scss";
import SeverityBadge from "../components/SeverityBadge";
import { recheckFinding } from "../../../../api/credentialScanning";

// Keys for known credential types / detection methods. Wire values stay
// verbatim; labels resolve through t() inside the component.
const REMEDIATION_TIP_KEYS = {
  aws_key:              "credentialScanning.remTipAwsKey",
  github_token:         "credentialScanning.remTipGithubToken",
  gitlab_token:         "credentialScanning.remTipGitlabToken",
  private_key:          "credentialScanning.remTipPrivateKey",
  db_connection_string: "credentialScanning.remTipDbConnectionString",
  db_password:          "credentialScanning.remTipDbPassword",
  api_key:              "credentialScanning.remTipApiKey",
  generic_secret:       "credentialScanning.remTipGenericSecret",
};

const DETECTION_KEYS = {
  regex:     "credentialScanning.detMethodRegex",
  entropy:   "credentialScanning.detMethodEntropy",
  pam_match: "credentialScanning.detMethodPamMatch",
  keyword:   "credentialScanning.detMethodKeyword",
};

const SOURCE_KEYS = {
  ssh_asset: "credentialScanning.detSrcSshAsset",
  rdp_asset: "credentialScanning.detSrcRdpWinSmb",
  database:  "credentialScanning.sourceDatabase",
  git_repo:  "credentialScanning.sourceGitRepository",
};

const FindingDetail = ({ finding, onClose, onAcknowledge, onFalsePositive, onRemediated }) => {
  const { t } = useTranslation();
  const [rechecking, setRechecking] = useState(false);
  const [recheckResult, setRecheckResult] = useState(null); // {still_present, message}

  const SOURCE_LABELS = useMemo(() => Object.fromEntries(
    Object.entries(SOURCE_KEYS).map(([k, key]) => [k, t(key)])
  ), [t]);
  const DETECTION_LABELS = useMemo(() => Object.fromEntries(
    Object.entries(DETECTION_KEYS).map(([k, key]) => [k, t(key)])
  ), [t]);

  // Reset recheck result when finding changes
  useEffect(() => { setRecheckResult(null); }, [finding?.id]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleRecheck = async () => {
    setRechecking(true);
    setRecheckResult(null);
    const { data, error } = await recheckFinding(finding.id);
    setRechecking(false);
    if (error) {
      setRecheckResult({ still_present: null, message: error });
      return;
    }
    setRecheckResult(data);
    // If auto-remediated, notify parent to refresh the list
    if (!data.still_present && onRemediated) {
      onRemediated(finding.id);
    }
  };

  if (!finding) return null;

  const tipKey = REMEDIATION_TIP_KEYS[finding.credential_type];
  const tip = tipKey ? t(tipKey) : t("credentialScanning.remTipDefault");

  return (
    <>
      {/* Backdrop — click outside to close. Layout/position in .finding-drawer-backdrop. */}
      <div onClick={onClose} className="finding-drawer-backdrop" />

      {/* Drawer panel — flex column, does NOT scroll itself. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="finding-drawer"
      >
        {/* Sticky header — always visible, never scrolls away. */}
        <div className="finding-drawer-header">
          <div className="finding-drawer-title-group">
            <SeverityBadge severity={finding.severity} />
            <span className="finding-drawer-title">
              {finding.credential_type?.replace(/_/g, " ")}
            </span>
          </div>

          <button
            onClick={onClose}
            title={t("credentialScanning.closeEsc")}
            className="finding-drawer-close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body — only this part scrolls. */}
        <div className="finding-drawer-body">
          {/* Location */}
          <Section title={t("credentialScanning.secLocation")}>
            <Row label={t("credentialScanning.rowSource")} value={SOURCE_LABELS[finding.source_type] || finding.source_type} />
            {finding.asset_name && <Row label={t("credentialScanning.rowAsset")} value={finding.asset_name} />}
            {finding.repository_name && <Row label={t("credentialScanning.rowRepository")} value={finding.repository_name} />}
            {finding.branch && <Row label={t("credentialScanning.rowBranch")} value={finding.branch} />}
            {finding.commit_hash && <Row label={t("credentialScanning.rowCommit")} value={finding.commit_hash.slice(0, 12)} />}
            <Row label={t("credentialScanning.rowFile")} value={finding.file_path} mono />
            {finding.line_number && <Row label={t("credentialScanning.rowLine")} value={finding.line_number} />}
          </Section>

          {/* Recheck — only for SSH assets */}
          {finding.source_type === "ssh_asset" && (
            <div className="finding-recheck-box">
              <div className="recheck-title">
                {t("credentialScanning.verifyCredential")}
              </div>
              <p className="recheck-desc">
                <Trans
                  i18nKey="credentialScanning.recheckDescription"
                  components={{ remediated: <strong /> }}
                />
              </p>
              <button
                onClick={handleRecheck}
                disabled={rechecking}
                className="finding-recheck-button"
              >
                {rechecking ? t("credentialScanning.rechecking") : t("credentialScanning.recheckNow")}
              </button>

              {recheckResult && (
                <div
                  className={`finding-recheck-result finding-recheck-result--${
                    recheckResult.still_present === null
                      ? "unknown"
                      : recheckResult.still_present
                      ? "present"
                      : "gone"
                  }`}
                >
                  {recheckResult.still_present === null && "⚠ "}
                  {recheckResult.still_present === true && "✗ "}
                  {recheckResult.still_present === false && "✓ "}
                  {recheckResult.message}
                </div>
              )}
            </div>
          )}

          {/* Detection */}
          <Section title={t("credentialScanning.secDetection")}>
            <Row label={t("credentialScanning.rowMethod")} value={DETECTION_LABELS[finding.detection_method] || finding.detection_method} />
            {finding.rule_name && <Row label={t("credentialScanning.rowRule")} value={finding.rule_name} />}
            {finding.entropy_score != null && (
              <Row label={t("credentialScanning.rowEntropy")} value={`${finding.entropy_score.toFixed(3)} ${t("credentialScanning.entropyValueSuffix")}`} />
            )}
            <Row label={t("credentialScanning.rowConfidence")} value={`${Math.round((finding.confidence_score || 0) * 100)}%`} />
          </Section>

          {/* Secret Preview */}
          <Section title={t("credentialScanning.secSecretPreview")}>
            <code className="finding-secret-preview">
              {finding.secret_preview}
            </code>
          </Section>

          {/* Context snippet */}
          {finding.context_snippet && (
            <Section title={t("credentialScanning.secContext")}>
              <pre className="cred-scan-context-snippet">{finding.context_snippet}</pre>
            </Section>
          )}

          {/* PAM Match warning */}
          {finding.matched_pam_username && (
            <div className="finding-pam-warning">
              <strong className="finding-pam-warning-title">⚠ {t("credentialScanning.pamWarningTitle")}</strong>
              <p>
                <Trans
                  i18nKey="credentialScanning.pamWarningMessage"
                  values={{ username: finding.matched_pam_username }}
                  components={{ user: <strong /> }}
                />
              </p>
            </div>
          )}

          {/* Remediation tip */}
          <Section title={t("credentialScanning.secRemediation")}>
            <p className="finding-remediation-text">{tip}</p>
          </Section>

          {/* Workflow actions */}
          {finding.status === "OPEN" && (
            <div className="finding-actions">
              <button
                onClick={onAcknowledge}
                className="finding-acknowledge-btn"
              >
                {t("credentialScanning.btnAcknowledge")}
              </button>
              <button
                onClick={onFalsePositive}
                className="finding-false-positive-btn"
              >
                {t("credentialScanning.btnFalsePositive")}
              </button>
            </div>
          )}

          {/* Metadata */}
          <Section title={t("credentialScanning.secMetadata")}>
            <Row label={t("credentialScanning.rowFirstSeen")} value={finding.first_seen ? new Date(finding.first_seen).toLocaleString() : "—"} />
            <Row label={t("credentialScanning.rowLastSeen")} value={finding.last_seen ? new Date(finding.last_seen).toLocaleString() : "—"} />
            <Row label={t("credentialScanning.rowStatus")} value={finding.status?.replace("_", " ")} />
            {finding.acknowledged_at && (
              <Row label={t("credentialScanning.rowAcknowledged")} value={new Date(finding.acknowledged_at).toLocaleString()} />
            )}
          </Section>
        </div>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="finding-section-title">{title}</div>
    {children}
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className={`finding-row${mono ? " finding-row--mono" : ""}`}>
    <span className="finding-row-label">{label}</span>
    <span className="finding-row-value">{value ?? "—"}</span>
  </div>
);

export default FindingDetail;
